import React, { useEffect, useMemo, useState } from "react";
import { useApp } from "../../context/AppState";
import {
  getOverlayStyle,
  getDraggablePopupAnimationAndShadowStyles,
  getDraggablePopupPositionStyles,
  resolvePreviewIsMobile,
} from "../../helper";
import {
  isWithinDisplaySchedule,
  normalizeDisplaySchedule,
} from "../../helper/displaySchedule";
import { POPUP_SECTIONS } from "../../constant/constant";
import { isSectionVisible } from "../../../shared/section-visibility.js";

/** Only gate popup visibility on countdown end when a COUNTDOWN block is present. */
function popupHasCountdownVisibilityGate(popupData) {
  const countdown = popupData?.generalSettings?.condition?.countdown;
  const endTime = countdown?.values?.countdownEndTime;
  if (!endTime) return false;
  if (String(countdown?.countdown_type || "").toLowerCase() !== "till_fixed_date") {
    return false;
  }

  const schema = popupData?.schema;
  if (!Array.isArray(schema)) return false;

  return schema.some((step) => {
    const sections = step?.sections || step?.section || [];
    return sections.some(
      (section) =>
        section?.type === POPUP_SECTIONS.COUNTDOWN &&
        isSectionVisible(section)
    );
  });
}
import { FormProvider } from "../FormProvider";
import { useForm } from "react-hook-form";
import RenderDraggablePopup from "./RenderDraggablePopup";
import AiRecommendationUpsellPopup from "../../layouts/ai-recommendation-upsell/AiRecommendationUpsellPopup";
import ProductRecommendationPopup from "../../layouts/product-recommendation/ProductRecommendationPopup";
import CustomCartDrawer from "../CartDrawer/CustomCartDrawer";
import {
  getCustomCartDrawerLayout,
  getStaticCartDrawerSteps,
  isStaticCartDrawerContentStep,
  isStaticCartDrawerTriggerStep,
  isStaticCustomCartDrawer,
} from "../../constants/custom-cart-drawer-template";
import {
  isStaticProductPageUpsell,
  isStaticProductPageUpsellContentStep,
  getStaticProductPageUpsellSections,
  STATIC_PRODUCT_PAGE_UPSELL_SECTION_INDEX,
} from "../../constants/product-page-upsell-template";
import ProductPageUpsellEditorPreview from "../../layouts/product-page-upsell/ProductPageUpsellEditorPreview";
import VolumeDiscountWidget, {
  isVolumeDiscountPreview,
} from "../../layouts/volume-discount/VolumeDiscountWidget";
import {
  isCartDrawerFbtBundleShell,
  isCartDrawerVerticalShell,
  resolveCartDrawerUpsellShell,
} from "../../../shared/product-shell-layout.js";
import {
  isExtensionListShell,
  resolveExtensionProductShell,
} from "../../../shared/extension-product-layout.js";
import {
  isPopupLayout,
  POPUP_LAYOUT_IDS,
} from "../../popup-layouts/index.js";
import { isUpsellRecommendationProductPopup } from "../../../shared/product-upsell-recommendation-defaults.js";
import { isProductRecommendationPopupLayout } from "../../../shared/product-recommendation-popup-schema.js";
import ErrorBoundary from "./ErrorBoundary";
import CheckoutExtensionPreviewLayout from "../../layouts/checkout-extension/CheckoutExtensionPreviewLayout";
import PostPurchaseUpsellPopup from "../../layouts/checkout-extension/post-purchase/PostPurchaseUpsellPopup";
import ExtensionSurfaceEditorPreview from "../../layouts/checkout-extension/ExtensionSurfaceEditorPreview";
import WidgetOnlyExtensionPreview from "../../layouts/checkout-extension/shared/WidgetOnlyExtensionPreview";
import WidgetOnlyCampaignPreview from "./WidgetOnlyCampaignPreview";
import {
  EXTENSION_PAGE,
  isExtensionEditorSurfacePreview,
  resolveExtensionSurface,
} from "../../../shared/extension-preview.js";
// Social Proof components are imported in RenderDraggablePopup.jsx

const RenderDynamicPopupPreview = ({
  popupData,
  popupState,
  setPopupState,
}) => {
  const { isMobile: viewportIsMobile, previewDeviceIsMobile, appState, cartOpen } = useApp();
  const isMobile = resolvePreviewIsMobile(
    appState,
    viewportIsMobile,
    previewDeviceIsMobile
  );

  const [showPopup, setShowPopup] = useState(false);

  const activeStep = useMemo(() => popupState?.activeStep, [popupState]);

  // Add schema validation and fallback
  const validatedSchema = useMemo(() => {
    if (!popupData?.schema || !Array.isArray(popupData.schema)) {
      console.warn('Invalid schema structure in popupData:', popupData);
      return [];
    }
    
    // Ensure each schema item has the correct structure
    return popupData.schema.map((item, index) => {
      if (!item) return null;
      
      const sectionsFromSections = Array.isArray(item.sections)
        ? item.sections
        : [];
      const sectionsFromSection = Array.isArray(item.section) ? item.section : [];
      // Prefer the richer source when both exist; some payloads keep stale `sections`.
      const sections =
        sectionsFromSection.length > sectionsFromSections.length
          ? sectionsFromSection
          : sectionsFromSections;
      
      return {
        ...item,
        sections: Array.isArray(sections) ? sections : [],
        popupSettings: item.popupSettings || {},
      };
    }).filter(Boolean);
  }, [popupData?.schema]);

  const popupSettings = useMemo(
    () => validatedSchema[activeStep]?.popupSettings || {},
    [validatedSchema, activeStep]
  );

  const activeSchemaStep = validatedSchema[activeStep];
  const isStaticCartTriggerStorefront =
    !appState?.is_preview_mode &&
    isStaticCustomCartDrawer(popupData) &&
    isStaticCartDrawerTriggerStep(activeSchemaStep);
  const hideStaticCartTrigger = isStaticCartTriggerStorefront && cartOpen;

  const useAiRecommendationUpsellRenderer = isPopupLayout(
    popupData,
    POPUP_LAYOUT_IDS.AI_RECOMMENDATION_UPSELL
  );

  const useProductRecommendationRenderer =
    isProductRecommendationPopupLayout(popupData) ||
    (isUpsellRecommendationProductPopup(popupData) &&
      !useAiRecommendationUpsellRenderer);

  const isCatalogModalEditorPreview =
    appState?.is_preview_mode &&
    (useProductRecommendationRenderer || useAiRecommendationUpsellRenderer);

  const embeddingData = useMemo(
    () => popupData?.generalSettings?.condition?.embed,
    [popupData]
  );

  const rootStyles = {
    position:
      !appState?.is_preview_mode &&
      embeddingData?.is_embedded &&
      (popupState?.hasOwnProperty("isEmbeded")
        ? activeStep > 0
          ? false
          : popupState.isEmbeded
        : true)
        ? "absolute"
        : "fixed",
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    pointerEvents:
      hideStaticCartTrigger ||
      isStaticCartTriggerStorefront ||
      popupSettings?.dynamicalOptions?.overlay_style === "None"
        ? "none"
        : "auto",
    zIndex: embeddingData?.is_embedded ? 1 : 2147483647,
    ...(hideStaticCartTrigger
      ? { visibility: "hidden" }
      : {}),
  };

  const drawerClosing = popupState?.drawerClosing === true;

  const useCatalogMobileDrawer =
    isMobile &&
    (useProductRecommendationRenderer || useAiRecommendationUpsellRenderer);

  const overlayStyles = useMemo(
    () => {
      const base = {
        position: "absolute",
        top: 0,
        left: 0,
        bottom: 0,
        right: 0,
      };

      if (isCatalogModalEditorPreview) {
        return {
          ...base,
          backgroundColor: "rgba(15, 23, 42, 0.42)",
          backdropFilter: "none",
          WebkitBackdropFilter: "none",
          ...(useCatalogMobileDrawer
            ? {
                animation: drawerClosing
                  ? "prProductDrawerBackdropOut 0.3s ease-in both"
                  : "prProductDrawerBackdropIn 0.28s ease-out both",
              }
            : {}),
        };
      }

      const overlayBase = getOverlayStyle(
        popupSettings?.dynamicalOptions?.overlay_style
      );
      if (useCatalogMobileDrawer) {
        return {
          ...base,
          ...overlayBase,
          animation: drawerClosing
            ? "prProductDrawerBackdropOut 0.3s ease-in both"
            : "prProductDrawerBackdropIn 0.28s ease-out both",
        };
      }

      return {
        ...base,
        ...overlayBase,
        ...(isStaticCartTriggerStorefront ? { pointerEvents: "none" } : {}),
      };
    },
    [
      isCatalogModalEditorPreview,
      isMobile,
      useCatalogMobileDrawer,
      drawerClosing,
      isStaticCartTriggerStorefront,
      popupSettings?.dynamicalOptions?.isHideBgOverlay,
      popupSettings?.dynamicalOptions?.overlay_style,
    ]
  );

  const relativeStyles = useMemo(() => {
    const padding =
      validatedSchema[activeStep]?.popupSettings?.dynamicalOptions?.window
        ?.padding?.[isMobile ? "mobile" : "desktop"] || 0;

    const base = {
      position: "absolute",
      top: 0,
      left: 0,
      bottom: 0,
      right: 0,
      padding: `${padding}px`,
    };

    if (useCatalogMobileDrawer) {
      return {
        ...base,
        padding: 0,
        pointerEvents: "none",
      };
    }

    return {
      ...base,
      ...getDraggablePopupPositionStyles(
        popupSettings?.dynamicalOptions,
        isMobile
      ),
      ...getDraggablePopupAnimationAndShadowStyles(
        popupSettings?.dynamicalOptions,
        isMobile
      ),
    };
  }, [
    validatedSchema,
    activeStep,
    isMobile,
    useCatalogMobileDrawer,
    popupSettings?.dynamicalOptions,
  ]);

  const methods = useForm({
    defaultValues: {},
    mode: "onSubmit",
  });

  const { handleSubmit } = methods;

  useEffect(() => {
    if (appState?.is_preview_mode) {
      setShowPopup(true);
      return;
    }

    const { countdownEndTime } =
      popupData?.generalSettings?.condition?.countdown?.values || {};

    const checkTime = () => {
      const currentTime = Date.now();
      const condition = popupData?.generalSettings?.condition || {};
      const schedule = normalizeDisplaySchedule(condition);
      let visible = isWithinDisplaySchedule(schedule);

      if (
        visible &&
        countdownEndTime &&
        popupHasCountdownVisibilityGate(popupData)
      ) {
        const countdownTime = new Date(countdownEndTime).getTime();
        visible = currentTime < countdownTime;
      }

      setShowPopup(visible);
    };

    checkTime();

    const intervalId = setInterval(checkTime, 1000);
    return () => clearInterval(intervalId);
  }, [
    popupData?.generalSettings?.condition,
    popupData?.generalSettings?.condition?.countdown?.values?.countdownEndTime,
    appState?.is_preview_mode,
  ]);

  if (!showPopup) return <></>;

  // Pass validated schema to child components
  const popupDataWithValidatedSchema = {
    ...popupData,
    schema: validatedSchema,
  };

  const { drawerStepIndex, hasTrigger } = getStaticCartDrawerSteps(
    popupDataWithValidatedSchema
  );
  const isDrawerOnlyCart = drawerStepIndex != null && !hasTrigger;
  const isStaticCartDrawerEditorPreview =
    appState?.is_preview_mode &&
    isStaticCustomCartDrawer(popupDataWithValidatedSchema) &&
    drawerStepIndex != null &&
    (isDrawerOnlyCart ||
      (activeStep === drawerStepIndex &&
        isStaticCartDrawerContentStep(activeSchemaStep)));

  if (isStaticCartDrawerEditorPreview) {
    const drawer = (
      <CustomCartDrawer
        open
        embeddedInEditor
        popupData={popupDataWithValidatedSchema}
        layout={getCustomCartDrawerLayout(popupDataWithValidatedSchema)}
      />
    );
    if (appState?.previewChrome === "widget") {
      return (
        <ErrorBoundary>
          <WidgetOnlyCampaignPreview>{drawer}</WidgetOnlyCampaignPreview>
        </ErrorBoundary>
      );
    }
    return (
      <ErrorBoundary>
        <div className="weu-custom-cart-drawer--editor-preview">{drawer}</div>
      </ErrorBoundary>
    );
  }

  const isVolumeDiscountEditorPreview =
    appState?.is_preview_mode &&
    isVolumeDiscountPreview(popupDataWithValidatedSchema);

  if (isVolumeDiscountEditorPreview) {
    return (
      <ErrorBoundary>
        {appState?.previewChrome === "widget" ? (
          <WidgetOnlyCampaignPreview fitContent>
            <VolumeDiscountWidget
              popup={popupDataWithValidatedSchema}
              previewDemoVariants
            />
          </WidgetOnlyCampaignPreview>
        ) : (
          <div style={{ padding: 16, maxWidth: 460, margin: "0 auto" }}>
            <VolumeDiscountWidget
              popup={popupDataWithValidatedSchema}
              previewDemoVariants
            />
          </div>
        )}
      </ErrorBoundary>
    );
  }

  const isProductPageUpsellEditorPreview =
    appState?.is_preview_mode &&
    isStaticProductPageUpsell(popupDataWithValidatedSchema) &&
    isStaticProductPageUpsellContentStep(activeSchemaStep);

  if (isProductPageUpsellEditorPreview) {
    const productPageSections = getStaticProductPageUpsellSections(
      popupDataWithValidatedSchema,
      activeStep
    );
    const productSection =
      productPageSections?.[STATIC_PRODUCT_PAGE_UPSELL_SECTION_INDEX.UPSELL_PRODUCT];
    const productPageShell = resolveCartDrawerUpsellShell(
      popupDataWithValidatedSchema,
      productSection
    );
    // FBT stack and carousel grid both need height: max-content so multi-row
    // content is not clipped inside the fixed 100vh widget canvas.
    const productPageFitContent =
      isCartDrawerFbtBundleShell(productPageShell) ||
      (isCartDrawerVerticalShell(productPageShell) &&
        productSection?.setting?.layout?.showAsGrid === true);

    return (
      <ErrorBoundary>
        <FormProvider
          popupData={popupDataWithValidatedSchema}
          popupState={popupState}
          methods={methods}
          onSubmit={handleSubmit(() => {})}
        >
          {appState?.previewChrome === "widget" ? (
            <WidgetOnlyCampaignPreview fitContent={productPageFitContent}>
              <ProductPageUpsellEditorPreview
                popupData={popupDataWithValidatedSchema}
                popupState={popupState}
                chrome="widget"
              />
            </WidgetOnlyCampaignPreview>
          ) : (
            <ProductPageUpsellEditorPreview
              popupData={popupDataWithValidatedSchema}
              popupState={popupState}
            />
          )}
        </FormProvider>
      </ErrorBoundary>
    );
  }

  // Drawer step UI is rendered exclusively by CartDrawerModal on the storefront.
  // Rendering it here as a draggable popup leaves dim overlay + header stuck after close.
  const isStaticCartDrawerStorefrontDrawerStep =
    !appState?.is_preview_mode &&
    isStaticCustomCartDrawer(popupDataWithValidatedSchema) &&
    drawerStepIndex != null &&
    activeStep === drawerStepIndex &&
    isStaticCartDrawerContentStep(activeSchemaStep);

  if (isStaticCartDrawerStorefrontDrawerStep) {
    return null;
  }

  const extensionSurface = resolveExtensionSurface(
    popupDataWithValidatedSchema,
    appState?.extensionSurface
  );

  if (appState?.is_preview_mode && isExtensionEditorSurfacePreview(extensionSurface)) {
    if (appState?.previewChrome === "widget") {
      const extensionShell = resolveExtensionProductShell(
        popupDataWithValidatedSchema
      );
      const extensionListFitContent = isExtensionListShell(extensionShell);

      return (
        <ErrorBoundary>
          <FormProvider
            popupData={popupDataWithValidatedSchema}
            popupState={popupState}
            methods={methods}
            onSubmit={handleSubmit(() => {})}
          >
            <WidgetOnlyExtensionPreview
              surface={extensionSurface}
              popupData={popupDataWithValidatedSchema}
              popupState={popupState}
              setPopupState={setPopupState}
              fitContent={extensionListFitContent}
            />
          </FormProvider>
        </ErrorBoundary>
      );
    }

    return (
      <ErrorBoundary>
        <CheckoutExtensionPreviewLayout
          surface={extensionSurface}
          isMobile={isMobile}
          variant="surface"
        >
          <FormProvider
            popupData={popupDataWithValidatedSchema}
            popupState={popupState}
            methods={methods}
            onSubmit={handleSubmit(() => {})}
          >
            {extensionSurface === EXTENSION_PAGE.POST_PURCHASE ? (
              <PostPurchaseUpsellPopup
                popupData={popupDataWithValidatedSchema}
                popupState={popupState}
                setPopupState={setPopupState}
              />
            ) : (
              <ExtensionSurfaceEditorPreview
                surface={extensionSurface}
                popupData={popupDataWithValidatedSchema}
                popupState={popupState}
                setPopupState={setPopupState}
              />
            )}
          </FormProvider>
        </CheckoutExtensionPreviewLayout>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <FormProvider
        popupData={popupDataWithValidatedSchema}
        popupState={popupState}
        methods={methods}
        onSubmit={handleSubmit(() => {})}
      >
        {appState?.previewChrome === "widget" ? (
          <WidgetOnlyCampaignPreview>
            {useProductRecommendationRenderer ? (
              <ProductRecommendationPopup
                popupData={popupDataWithValidatedSchema}
                popupState={popupState}
                setPopupState={setPopupState}
              />
            ) : useAiRecommendationUpsellRenderer ? (
              <AiRecommendationUpsellPopup
                popupData={popupDataWithValidatedSchema}
                popupState={popupState}
                setPopupState={setPopupState}
              />
            ) : (
              <RenderDraggablePopup
                popupData={popupDataWithValidatedSchema}
                popupState={popupState}
                setPopupState={setPopupState}
              />
            )}
          </WidgetOnlyCampaignPreview>
        ) : (
          <div style={rootStyles}>
            <div style={overlayStyles} />
            <div key={activeStep} style={relativeStyles}>
              {useProductRecommendationRenderer ? (
                <ProductRecommendationPopup
                  popupData={popupDataWithValidatedSchema}
                  popupState={popupState}
                  setPopupState={setPopupState}
                />
              ) : useAiRecommendationUpsellRenderer ? (
                <AiRecommendationUpsellPopup
                  popupData={popupDataWithValidatedSchema}
                  popupState={popupState}
                  setPopupState={setPopupState}
                />
              ) : (
                <RenderDraggablePopup
                  popupData={popupDataWithValidatedSchema}
                  popupState={popupState}
                  setPopupState={setPopupState}
                />
              )}
            </div>
          </div>
        )}
      </FormProvider>
    </ErrorBoundary>
  );
};

export default RenderDynamicPopupPreview;
